import React from 'react';
import { ThemeProvider } from 'emotion-theming';
import P from 'prop-types';
import axios from 'axios';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';

import { toInsecureApiUri } from '../utils/url';
import { setTitle } from '../utils/title';
import { generateTheme } from '../UI/themes/generator';

export const ConfigurationContext = React.createContext();

export const withConfiguration = Component =>
   class WithConfiguration extends React.Component {
      render() {
         return (
            <ConfigurationContext.Consumer>
               {value => <Component {...this.props} configuration={value} />}
            </ConfigurationContext.Consumer>
         );
      }
   };

export const defaultSettings = {
   appName: 'Patient Access Portal',
   disableNotesToOffice: false,
   theme: {
      name: 'default',
      primary: {
         100: '#bad3ec',
         200: '#8eb8df',
         300: '#5694d0',
         400: '#3f85c9',
         500: '#3376b6',
         600: '#2c669e',
         700: '#265786',
         800: '#1f476e',
         900: '#183856'
      },
      accent: {
         100: '#F3AB79',
         200: '#F19D61',
         300: '#EF8E4A',
         400: '#ED8032',
         500: '#EB711B',
         600: '#D96513',
         700: '#C25A11',
         800: '#AB4F0F',
         900: '#93440D'
      },
      highlight: {
         primary: '#DFEAF2',
         secondary: '#96BAD5'
      }
   },
   allowCCPayment: true,
   allowACHPayment: true
};

const noACHPaymentRegex = /https?:\/\/[a-zA-Z0-9]*-pay\..*\/?\w*/;

export class ConfigurationProvider extends React.Component {
   state = {
      settings: defaultSettings,
      loaded: false
   };

   static propTypes = {
      prefix: P.string.isRequired,
      children: P.node
   };

   getMuiTheme = theme =>
      createMuiTheme({
         typography: {
            fontFamily: "'Open Sans', sans-serif",
            htmlFontSize: 12,
            button: {
               fontSize: '1.2rem',
               fontWeight: 600,
               textTransform: 'none',
               color: theme.palettes.primary(500)
            }
         },
         palette: {
            primary: {
               main: theme.palettes.primary(500)
            },
            secondary: {
               main: theme.palettes.accent(500)
            },
            text: {
               primary: theme.text.colors.default,
               disabled: theme.text.colors.disabled
            }
         },
         overrides: {
            MuiButton: {
               contained: {
                  color: theme.palettes.primary(500),
                  '&$disabled': {
                     backgroundColor: theme.palettes.background(1000)
                  }
               }
            },
            MuiDialogTitle: {
               root: {
                  padding: '24px 24px 18px 24px',
                  color: theme.text.colors.default
               }
            },
            MuiDialogContent: {
               root: {
                  color: theme.text.colors.default
               }
            },
            MuiDialogActions: {
               root: {
                  padding: '0 20px 24px 20px',
                  margin: '0'
               }
            },
            MuiRadio: {
               root: {
                  width: '24px',
                  height: '30px',
                  marginRight: '6px'
               }
            }
         }
      });

   componentDidMount() {
      return axios
         .get(toInsecureApiUri(`config/${this.props.prefix}`))
         .then(response => {
            const settings = {
               ...defaultSettings,
               ...response.data,
               allowACHPayment: !noACHPaymentRegex.test(window.location.href)
            };

            setTitle(settings.appName);
            this.setState({ settings, hasFailed: false, loaded: true });
         })
         .catch(() => {
            this.setState({ settings: defaultSettings, hasFailed: true, loaded: true });
         });
   }

   render() {
      const { theme: palette } = this.state.settings;
      const theme = palette
         ? generateTheme(palette.name, palette.primary, palette.accent, palette.highlight)
         : {};
      const muiTheme = palette ? this.getMuiTheme(theme) : undefined;
      return (
         <ConfigurationContext.Provider value={this.state}>
            <MuiThemeProvider theme={muiTheme}>
               <ThemeProvider theme={theme}>{this.props.children}</ThemeProvider>
            </MuiThemeProvider>
         </ConfigurationContext.Provider>
      );
   }
}



// WEBPACK FOOTER //
// ./src/contexts/ConfigurationContext.js